// React and hooks
import { useCallback, useEffect, useState } from "react";

// Helpers
import { transformRoute, useTranslate } from "helpers";

// Types
import { PriceListSearchPageCmp, PriceListSearchPageProps } from "./types";

// UI Components
import { Breadcrumb } from "ui/components/Breadcrumb";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { NoResults } from "ui/components/NoResults";
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import { useSelectedItemsChanges } from "ui/components/Table/hooks";

// Services and API
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";
import { getPriceListsAPI } from "services/api/prices/api";

// Navigation
import { Routes } from "ui/modules/prices/navigation";

// API Data Types
import {
  PriceListRow,
  PriceListSearchFormValues,
} from "services/api/prices/types";

// Constants
import {
  PRICE_LIST_CREATE_PAGE_TITLE,
  PRICE_LIST_SEARCH_PAGE_TITLE,
} from "ui/components/PageTitle/consts";

// Components
import PriceListSearchFilters from "./components/PriceListSearchFilters/PriceListSearchFilters";
import PriceListSearchResults from "./components/PriceListSearchResults/PriceListSearchResults";
import PriceListEditCard from "../PriceListEditCard/PriceListEditCard";
import { useNavigate } from "react-router-dom";

const PriceListSearchPage: PriceListSearchPageCmp = (
  props: PriceListSearchPageProps
) => {
  const { t } = useTranslate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<PriceListRow>
  >(initialDataWithPagination);

  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [priceListId, setPriceListId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);

  const navigate = useNavigate();

  useEffect(() => {
    fetchSearchResult(searchResults.pagination);
  }, []);

  const fetchSearchResult = async (
    pagination: Pagination,
    name?: string | null
  ) => {
    setIsLoading(true);
    try {
      const results = await getPriceListsAPI(pagination, name);

      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleRowClick = useCallback(async (id: string) => {
    navigate(
      transformRoute(Routes.PriceListDetailPageRouter, {
        priceListId: id,
      })
    );
  }, []);

  const handleAddNew = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setPriceListId("");
      setShowAddNewModal(true);
    },
    []
  );

  const handleSearchChange = useCallback(
    (searchFormValues: PriceListSearchFormValues) => {
      fetchSearchResult(searchResults.pagination, searchFormValues.search);
    },
    []
  );

  const handleEditSuccess = useCallback(() => {
    fetchSearchResult(searchResults.pagination);
    setPriceListId(null);
  }, []);

  const handleSelectedChange = useSelectedItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handlePaginationChange = (newPagination: Pagination) => {
    fetchSearchResult(newPagination);
  };

  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md border border-b mb-2">
          <PageHeader>
            <PageTitle
              title={PRICE_LIST_SEARCH_PAGE_TITLE.text}
              icon={PRICE_LIST_SEARCH_PAGE_TITLE.icon}
              iconColor={PRICE_LIST_SEARCH_PAGE_TITLE.iconColor}
            />
            <Breadcrumb />
          </PageHeader>
        </div>
        <div className="px-0 pt-2 mx-[-1px] xl:px-6">
          <PriceListSearchFilters
            onSearchChange={handleSearchChange}
            onAddNew={handleAddNew}
          />
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults
              message={t(
                "admin.calculation.setting.pricelist.search.noresults"
              )}
            />
          ) : (
            <PriceListSearchResults
              searchResults={searchResults}
              selectedRows={selectedRows}
              isLoading={isLoading}
              onRowClick={handleRowClick}
              onPaginationChange={handlePaginationChange}
              onSelectedChange={handleSelectedChange}
            />
          )}
        </div>
      </div>
      {showAddNewModal && (
        <Modal
          show={showAddNewModal}
          onHide={() => setShowAddNewModal(false)}
          size="full"
        >
          <ModalHeader
            title={PRICE_LIST_CREATE_PAGE_TITLE.text}
            onHide={() => setShowAddNewModal(false)}
          />
          <ModalBody>
            <PriceListEditCard
              priceListId={priceListId}
              onSuccess={handleEditSuccess}
            />
          </ModalBody>
        </Modal>
      )}
    </>
  );
};

PriceListSearchPage.route = Routes.PriceListSearchPageRouter;

export default PriceListSearchPage;
